import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FileText,
  Users,
  Megaphone,
  LayoutDashboard,
  Newspaper,
  Settings,
  BarChart3,
  MessageSquare,
  Clock,
  Plus,
  Star,
} from 'lucide-react'
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command'
import { api } from '@/api/client'
import { isFeatureHidden } from '@/config/features'
import {
  ADVANCED_PAGE,
  SETTINGS_FIELDS,
  SETTINGS_PAGES,
  settingsHref,
} from '@/pages/settings/settingsPages'
import type { LibraryEntry } from '@/stores/contentGridItem'
import type { Lead, CampaignDTO } from '@/types'

const RECENT_SEARCHES_KEY = 'maxflow_recent_searches'
const MAX_RECENT_SEARCHES = 5
/** Abort every still-pending source after this long so a hung endpoint can
 *  never pin the palette on "Searching..." — the reported symptom. */
const SEARCH_TIMEOUT_MS = 10_000

/** The subset of GMBReviewResponse the palette renders. */
interface PaletteReview {
  id: string
  reviewer_name: string | null
  rating: number
  comment: string | null
}

interface SearchResult {
  id: string
  type: 'page' | 'lead' | 'content' | 'campaign'
  title: string
  subtitle?: string
  url: string
  /** Feature-flag key — entry is dropped when isFeatureHidden(featureKey). */
  featureKey?: string
  /** Other words that find it. */
  keywords?: string[]
}

interface RecentSearch {
  query: string
  timestamp: number
}

// Static pages for navigation
const allPages: SearchResult[] = [
  { id: 'approvals', type: 'page', title: 'Approvals', url: '/approvals' },
  { id: 'dashboard', type: 'page', title: 'Dashboard', url: '/dashboard' },
  { id: 'files', type: 'page', title: 'Library', url: '/library' },
  { id: 'leads', type: 'page', title: 'Leads', subtitle: 'CRM', url: '/leads' },
  { id: 'contacts', type: 'page', title: 'Customers', subtitle: 'CRM', url: '/crm/contacts' },
  { id: 'bookings', type: 'page', title: 'Bookings', subtitle: 'CRM', url: '/crm/bookings' },
  {
    id: 'content',
    type: 'page',
    title: 'Content',
    subtitle: 'Content',
    url: '/content',
  },
  {
    id: 'content-calendar',
    type: 'page',
    title: 'Calendar',
    subtitle: 'Content',
    url: '/calendar',
  },
  {
    id: 'media-library',
    type: 'page',
    title: 'Media Library',
    subtitle: 'Content',
    url: '/media',
  },
  {
    id: 'campaigns',
    type: 'page',
    title: 'Campaigns',
    subtitle: 'Ads',
    url: '/ads/campaigns',
    featureKey: 'ads',
  },
  {
    id: 'ads-performance',
    type: 'page',
    title: 'Ads Performance',
    subtitle: 'Ads',
    url: '/ads/performance',
    featureKey: 'ads',
  },
  { id: 'gmb-reviews', type: 'page', title: 'Reviews', subtitle: 'GMB', url: '/inbox?tab=reviews' },
  { id: 'gmb-posts', type: 'page', title: 'GMB Posts', subtitle: 'GMB', url: '/gmb/posts' },
  {
    id: 'gmb-insights',
    type: 'page',
    title: 'GMB Insights',
    subtitle: 'GMB',
    url: '/gmb/insights',
  },
  {
    id: 'attribution-links',
    type: 'page',
    title: 'Attribution Links',
    url: '/attribution/links',
    featureKey: 'attribution',
  },
  { id: 'analytics', type: 'page', title: 'Results', url: '/results' },
  { id: 'settings', type: 'page', title: 'Settings', url: '/settings' },
  { id: 'notifications', type: 'page', title: 'Notifications', url: '/notifications' },
]

// Every Settings page, and every field on them, jumps straight to its section.
const settingsResults: SearchResult[] = [
  ...[...SETTINGS_PAGES, ADVANCED_PAGE].map((page) => ({
    id: `settings-page-${page.key}`,
    type: 'page' as const,
    title: page.label,
    subtitle: 'Settings',
    url: settingsHref(page.key),
    keywords: [page.summary],
  })),
  ...SETTINGS_FIELDS.map((field) => ({
    id: `settings-${field.page}-${field.anchor}-${field.label}`,
    type: 'page' as const,
    title: field.label,
    subtitle: 'Settings',
    url: settingsHref(field.page, field.anchor),
    keywords: field.keywords,
  })),
]

const pages: SearchResult[] = [...allPages, ...settingsResults].filter(
  (page) => !page.featureKey || !isFeatureHidden(page.featureKey)
)

// Quick actions
interface QuickAction {
  id: string
  title: string
  url: string
  icon: React.ElementType
  featureKey?: string
}

const allQuickActions: QuickAction[] = [
  { id: 'new-lead', title: 'Create New Lead', url: '/leads?action=new', icon: Users },
  {
    id: 'new-content',
    title: 'Create Content',
    url: '/chat',
    icon: FileText,
  },
  {
    id: 'new-campaign',
    title: 'Create Campaign',
    url: '/ads/campaigns/new',
    icon: Megaphone,
    featureKey: 'ads',
  },
]

const quickActions = allQuickActions.filter(
  (action) => !action.featureKey || !isFeatureHidden(action.featureKey)
)

function getIconForPage(pageId: string) {
  if (pageId.startsWith('settings') || pageId.includes('notification')) return Settings
  if (pageId.includes('dashboard')) return LayoutDashboard
  if (pageId.includes('lead') || pageId.includes('contact') || pageId.includes('booking'))
    return Users
  if (pageId.includes('content') || pageId.includes('media') || pageId.includes('brand'))
    return FileText
  if (pageId.includes('campaign') || pageId.includes('ads') || pageId.includes('performance'))
    return Megaphone
  if (pageId.includes('conversation') || pageId.includes('knowledge')) return MessageSquare
  if (pageId.includes('analytics') || pageId.includes('gmb') || pageId.includes('attribution'))
    return BarChart3
  return LayoutDashboard
}

interface SearchCommandPaletteProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function SearchCommandPalette({
  open: controlledOpen,
  onOpenChange,
}: SearchCommandPaletteProps) {
  const [internalOpen, setInternalOpen] = useState(false)

  // Use controlled state if provided, otherwise internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen
  const setOpen = onOpenChange || setInternalOpen
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [leads, setLeads] = useState<Lead[]>([])
  const [campaigns, setCampaigns] = useState<CampaignDTO[]>([])
  const [posts, setPosts] = useState<LibraryEntry[]>([])
  const [reviews, setReviews] = useState<PaletteReview[]>([])
  // Loaded lazily so no mount effect is needed just to hydrate from storage.
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>(() => {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
    if (!stored) return []
    try {
      return JSON.parse(stored)
    } catch {
      // Invalid JSON, reset
      localStorage.removeItem(RECENT_SEARCHES_KEY)
      return []
    }
  })
  const navigate = useNavigate()

  // Global keyboard shortcut (Cmd/Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(!open)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, setOpen])

  // Save recent search
  const saveRecentSearch = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return

    setRecentSearches((prev) => {
      const filtered = prev.filter((s) => s.query !== searchQuery)
      const updated = [{ query: searchQuery, timestamp: Date.now() }, ...filtered].slice(
        0,
        MAX_RECENT_SEARCHES
      )
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
      return updated
    })
  }, [])

  // Search data when query changes
  useEffect(() => {
    // One controller covers both stale-query cancellation (cleanup aborts it)
    // and the hard timeout, so an in-flight response from an earlier keystroke
    // can never overwrite a newer query's results.
    const controller = new AbortController()
    let stale = false

    // Clearing runs through a 0ms timeout (not the effect body) purely to keep
    // every setState in a callback; the debounce only applies to real queries.
    const searchTimeout = setTimeout(
      async () => {
        if (!query.trim()) {
          setLeads([])
          setCampaigns([])
          setPosts([])
          setReviews([])
          setLoading(false)
          return
        }
        setLoading(true)
        const timeoutTimer = setTimeout(() => controller.abort(), SEARCH_TIMEOUT_MS)
        const { signal } = controller

        // allSettled, not all: one failing source must not blank the sources
        // that succeeded — each rejection degrades to an empty group instead.
        const [leadsRes, campaignsRes, postsRes, reviewsRes] = await Promise.allSettled([
          api.getList<Lead>('/crm/leads', { params: { search: query, pageSize: 5 }, signal }),
          isFeatureHidden('ads')
            ? Promise.resolve({ data: [] as CampaignDTO[] })
            : api.getList<CampaignDTO>('/ads/campaigns', {
                params: { search: query, limit: 5 },
                signal,
              }),
          // Published posts — /content/library returns a bare array.
          api.get<LibraryEntry[]>('/content/library', { params: { q: query, limit: 5 }, signal }),
          api.getList<PaletteReview>('/gmb/reviews', { params: { q: query, limit: 5 }, signal }),
        ])
        clearTimeout(timeoutTimer)
        if (stale) return // a newer keystroke owns the palette state now

        setLeads(leadsRes.status === 'fulfilled' ? leadsRes.value.data : [])
        setCampaigns(campaignsRes.status === 'fulfilled' ? campaignsRes.value.data : [])
        setPosts(
          postsRes.status === 'fulfilled' && Array.isArray(postsRes.value)
            ? (postsRes.value as LibraryEntry[]).slice(0, 5)
            : []
        )
        setReviews(reviewsRes.status === 'fulfilled' ? reviewsRes.value.data : [])

        // Log each failed source individually so a flaky endpoint stays
        // diagnosable without blanking the whole palette.
        const sources = ['leads', 'campaigns', 'posts', 'reviews']
        ;[leadsRes, campaignsRes, postsRes, reviewsRes].forEach((res, i) => {
          if (res.status === 'rejected') {
            console.error(`Search source "${sources[i]}" failed:`, res.reason)
          }
        })
        setLoading(false)
      },
      query.trim() ? 300 : 0
    )

    return () => {
      stale = true
      clearTimeout(searchTimeout)
      controller.abort()
    }
  }, [query])

  // Handle selection
  const handleSelect = useCallback(
    (url: string, searchQuery?: string) => {
      if (searchQuery) {
        saveRecentSearch(searchQuery)
      }
      setOpen(false)
      setQuery('')
      navigate(url)
    },
    [navigate, saveRecentSearch, setOpen]
  )

  // Filter pages based on query
  const filteredPages = query
    ? pages.filter(
        (page) =>
          page.title.toLowerCase().includes(query.toLowerCase()) ||
          page.subtitle?.toLowerCase().includes(query.toLowerCase()) ||
          page.keywords?.some((word) => word.toLowerCase().includes(query.toLowerCase()))
      )
    : []

  const hasResults =
    filteredPages.length > 0 ||
    leads.length > 0 ||
    campaigns.length > 0 ||
    posts.length > 0 ||
    reviews.length > 0
  const showRecent = !query && recentSearches.length > 0

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Search"
      description="Search across pages, leads, content, posts, reviews, and campaigns"
      showCloseButton={false}
    >
      <CommandInput
        placeholder="Search pages, leads, content, posts, reviews..."
        value={query}
        onValueChange={setQuery}
        data-testid="search-input"
      />
      <CommandList>
        {loading && (
          <div className="text-muted-foreground py-6 text-center text-sm">Searching...</div>
        )}

        {!loading && query && !hasResults && <CommandEmpty>No results found.</CommandEmpty>}

        {/* Recent Searches */}
        {showRecent && (
          <CommandGroup heading="Recent Searches">
            {recentSearches.map((recent) => (
              <CommandItem
                key={recent.timestamp}
                value={`recent-${recent.query}`}
                onSelect={() => setQuery(recent.query)}
                data-testid={`recent-search-${recent.query}`}
              >
                <Clock className="text-muted-foreground mr-2 h-4 w-4" />
                <span>{recent.query}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Quick Actions - show when no query */}
        {!query && (
          <CommandGroup heading="Quick Actions">
            {quickActions.map((action) => {
              const Icon = action.icon
              return (
                <CommandItem
                  key={action.id}
                  value={action.id}
                  onSelect={() => handleSelect(action.url)}
                  data-testid={`action-${action.id}`}
                >
                  <Plus className="text-muted-foreground mr-2 h-4 w-4" />
                  <Icon className="mr-2 h-4 w-4" />
                  <span>{action.title}</span>
                </CommandItem>
              )
            })}
          </CommandGroup>
        )}

        {/* Pages */}
        {filteredPages.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Pages">
              {filteredPages.slice(0, 5).map((page) => {
                const Icon = getIconForPage(page.id)
                return (
                  <CommandItem
                    key={page.id}
                    // cmdk re-filters on the value, so it carries the words that found it.
                    value={`page-${page.id} ${page.keywords?.join(' ') ?? ''}`}
                    onSelect={() => handleSelect(page.url, query)}
                    data-testid={`page-${page.id}`}
                  >
                    <Icon className="text-muted-foreground mr-2 h-4 w-4" />
                    <span>{page.title}</span>
                    {page.subtitle && (
                      <span className="text-muted-foreground ml-2 text-xs">{page.subtitle}</span>
                    )}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </>
        )}

        {/* Leads */}
        {leads.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Leads">
              {leads.map((lead) => (
                <CommandItem
                  key={lead.id}
                  value={`lead-${lead.id}-${lead.name}`}
                  onSelect={() => handleSelect(`/leads/${lead.id}`, query)}
                  data-testid={`lead-${lead.id}`}
                >
                  <Users className="text-muted-foreground mr-2 h-4 w-4" />
                  <span>{lead.name}</span>
                  <span className="text-muted-foreground ml-2 text-xs">{lead.email}</span>
                  {lead.score === 'hot' && <Star className="text-warning ml-auto h-4 w-4" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Published posts */}
        {posts.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Posts">
              {posts.map((post) => (
                <CommandItem
                  key={post.id}
                  // cmdk re-filters items against its own input, so the value
                  // must carry the text the server matched on (title OR body).
                  value={`post-${post.id}-${post.title ?? ''}-${post.body?.slice(0, 120) ?? ''}`}
                  onSelect={() => handleSelect(`/content/post/${post.id}`, query)}
                  data-testid={`post-${post.id}`}
                >
                  <Newspaper className="text-muted-foreground mr-2 h-4 w-4" />
                  <span>{post.title || post.body?.slice(0, 60) || 'Untitled post'}</span>
                  {post.platforms.length > 0 && (
                    <span className="text-muted-foreground ml-2 text-xs capitalize">
                      {post.platforms.join(', ')}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* GMB reviews */}
        {reviews.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Reviews">
              {reviews.map((review) => (
                <CommandItem
                  key={review.id}
                  value={`review-${review.id}-${review.reviewer_name ?? ''}-${review.comment?.slice(0, 120) ?? ''}`}
                  onSelect={() => handleSelect('/inbox?tab=reviews', query)}
                  data-testid={`review-${review.id}`}
                >
                  <Star className="text-muted-foreground mr-2 h-4 w-4" />
                  <span className="whitespace-nowrap">{review.reviewer_name || 'Anonymous'}</span>
                  <span className="text-muted-foreground ml-2 truncate text-xs">
                    {review.rating}★{review.comment ? ` · ${review.comment.slice(0, 60)}` : ''}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Campaigns */}
        {campaigns.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Campaigns">
              {campaigns.map((campaign) => (
                <CommandItem
                  key={campaign.id}
                  value={`campaign-${campaign.id}-${campaign.name}`}
                  onSelect={() => handleSelect(`/ads/campaigns/${campaign.id}`, query)}
                  data-testid={`campaign-${campaign.id}`}
                >
                  <Megaphone className="text-muted-foreground mr-2 h-4 w-4" />
                  <span>{campaign.name}</span>
                  <span className="text-muted-foreground ml-2 text-xs capitalize">
                    {campaign.platform}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Keyboard hint at bottom */}
        {!query && (
          <>
            <CommandSeparator />
            <div className="text-muted-foreground flex items-center justify-between px-4 py-2 text-xs">
              <span>Navigate with arrow keys</span>
              <div className="flex items-center gap-2">
                <CommandShortcut>↵ to select</CommandShortcut>
                <CommandShortcut>esc to close</CommandShortcut>
              </div>
            </div>
          </>
        )}
      </CommandList>
    </CommandDialog>
  )
}
